import { useState } from 'react';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Dropdown, Layout, Menu, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { ROLE_LABELS } from '@goelectric/ui-components';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { isNavDivider, isNavGroup, visibleMenuEntries, visibleMenuItems } from './navigation';
import { ThemeToggle } from './ThemeToggle';

const { Content, Header, Sider } = Layout;

const PAGE_TITLES: Record<string, string> = {
  '/noc': 'Centro de Operaciones de Red (NOC)',
  '/laboratorio': 'Laboratorio',
  '/aliados': 'Aliados',
  '/clientes': 'Clientes',
  '/reportes': 'Reportes',
  '/flotas': 'Flotas',  '/dooh/medios': 'Biblioteca de medios',
  '/dooh/emision': 'Emisión global',
  '/dooh/pantallas': 'Pantallas',
  '/ubicaciones': 'Ubicaciones',
  '/cargadores': 'Cargadores',
  '/tarifas': 'Tarifas',
  '/packs': 'Packs de energía',
  '/membresias': 'Membresías',
};

/** Layout del Admin Web: sidebar oscura, header y contenido. */
export function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  // Fallback de marca: si `/brand/xgoe-mark.png` no existe, se conserva el cuadrado dorado.
  const [hasBrandMark, setHasBrandMark] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // El menú refleja el rol del usuario; la ruta se protege aparte (RequireRole).
  // Los grupos ponen encabezado a secciones con varias hojas (antd `type: 'group'`)
  // y los dividers separan los bloques (Operación / Catálogo / Pantallas Digitales).
  const menuItems: MenuProps['items'] = visibleMenuEntries(user?.role).map((entry) => {
    if (isNavGroup(entry)) {
      return {
        type: 'group',
        key: entry.key,
        label: entry.label,
        children: entry.children.map(({ key, icon, label }) => ({ key, icon, label })),
      };
    }
    if (isNavDivider(entry)) {
      return { type: 'divider', key: entry.key };
    }
    return { key: entry.key, icon: entry.icon, label: entry.label };
  });
  const selectedKey = visibleMenuItems(user?.role).find((item) =>
    location.pathname.startsWith(item.key),
  )?.key;
  const currentTitle = (selectedKey && PAGE_TITLES[selectedKey]) || 'xGoElectric';

  const userMenu: MenuProps = {
    items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Cerrar sesión', danger: true }],
    onClick: ({ key }) => {
      if (key === 'logout') {
        void logout();
      }
    },
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        breakpoint="md"
        collapsedWidth={72}
        width={240}
        style={{ background: 'var(--ge-color-shell-bg)', borderRight: '1px solid var(--ge-color-shell-border)' }}
      >
        <div className="app-brand">
          {hasBrandMark ? (
            <img
              src="/brand/xgoe-mark.png"
              alt=""
              className="app-brand__mark app-brand__mark--img"
              onError={() => setHasBrandMark(false)}
            />
          ) : (
            <span className="app-brand__mark" aria-hidden="true" />
          )}
          {!collapsed && (
            <span className="app-brand__text">
              xGo<strong>Electric</strong>
            </span>
          )}
        </div>
        <Menu
          className="app-menu"
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ background: 'transparent', borderInlineEnd: 'none' }}
        />
      </Sider>

      <Layout>
        <Header
          className="app-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            background: 'var(--ge-color-header-bg)',
            borderBottom: '1px solid var(--ge-color-border)',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Space size="middle">
            <Button
              type="text"
              aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((previous) => !previous)}
            />
            <Typography.Text strong className="app-header__title">
              {currentTitle}
            </Typography.Text>
          </Space>
          <Space size="large">
            <ThemeToggle />
            <Dropdown menu={userMenu} trigger={['click']} placement="bottomRight">
              <button type="button" className="app-user">
                <Avatar size="small" icon={<UserOutlined />} />
                <span className="app-user__email">{user?.email}</span>
                {user && <span className="app-user__role">{ROLE_LABELS[user.role]}</span>}
              </button>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: 24, background: 'var(--ge-color-bg)', minHeight: 'calc(100vh - 64px)' }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
